import React, { useState, useCallback, useRef } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  ReactFlowProvider,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { omniSearch, fetchDocumentImpact } from '../services/dashboardService';
import { usePinToBoard } from '../contexts/PinToBoardContext';
import EntityContextWindow from './EntityContextWindow';
import CommunityDetailPanel from './CommunityDetailPanel';
import DocumentDNADetail from './DocumentDNADetail';
import '../styles/OmniSearch.css';

// ── Node Type Config ───────────────────────────────────────

const NODE_TYPE_CONFIG = {
  listing: {
    color: '#059669',
    bg: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    icon: 'fas fa-building',
    label: 'Listing',
    minimap: '#059669',
  },
  document: {
    color: '#d97706',
    bg: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    icon: 'fas fa-file-alt',
    label: 'Document',
    minimap: '#d97706',
  },
  market_index: {
    color: '#dc2626',
    bg: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    icon: 'fas fa-chart-line',
    label: 'Market Index',
    minimap: '#dc2626',
  },
  // knowledge node types
  location: {
    color: '#6366f1',
    bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    icon: 'fas fa-map-marker-alt',
    label: 'Location',
    minimap: '#6366f1',
  },
  person: {
    color: '#8b5cf6',
    bg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    icon: 'fas fa-user',
    label: 'Person',
    minimap: '#8b5cf6',
  },
  organization: {
    color: '#0ea5e9',
    bg: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    icon: 'fas fa-sitemap',
    label: 'Organization',
    minimap: '#0ea5e9',
  },
  entity: {
    color: '#6366f1',
    bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    icon: 'fas fa-circle-nodes',
    label: 'Entity',
    minimap: '#6366f1',
  },
  community: {
    color: '#f59e0b',
    bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    icon: 'fas fa-people-group',
    label: 'Community',
    minimap: '#f59e0b',
  },
};

const getNodeConfig = (type) => {
  if (!type) return NODE_TYPE_CONFIG.entity;
  const key = type.toLowerCase().replace(/[^a-z_]/g, '');
  return NODE_TYPE_CONFIG[key] || NODE_TYPE_CONFIG.entity;
};

// ── Edge Type Config ───────────────────────────────────────

const EDGE_TYPE_CONFIG = {
  cross_source_bridge: { stroke: '#10b981', dash: '6 3', label: 'bridge' },
  document_link:       { stroke: '#f59e0b', dash: '5 3', label: 'doc→entity' },
  knowledge_edge:      { stroke: '#94a3b8', dash: null,  label: 'knowledge' },
};

const getEdgeStyle = (type) => {
  const cfg = EDGE_TYPE_CONFIG[type] || { stroke: '#94a3b8', dash: null };
  return {
    stroke: cfg.stroke,
    strokeWidth: 1.5,
    strokeDasharray: cfg.dash || undefined,
  };
};

// ── Omni type → Whiteboard item_type ─────────────────────
// Returns null for community (ephemeral, not pinnable).
// Defaults to 'knowledge_node' for ANY unrecognised KG node type
// (location, person, organization, entity, knowledge_node, COMPANY, PROPERTY …)
const getOmniItemType = (type) => {
  const t = (type || '').toLowerCase().replace(/[^a-z_]/g, '');
  if (t === 'listing')      return 'intelligence';
  if (t === 'document')     return 'pdf';
  if (t === 'market_index') return 'market_data';
  if (t === 'community')    return null;          // ephemeral — no pin
  return 'knowledge_node';                        // all other KG node types
};

// ── Custom Node Component ──────────────────────────────────

const OmniNode = ({ data, selected }) => {
  const cfg = getNodeConfig(data.type);
  const label = data.label || data.type || 'Node';
  const shortLabel = label.length > 30 ? label.slice(0, 28) + '…' : label;
  const subLine = data.subLabel || null;

  return (
    <div
      className={`os-node${selected ? ' selected' : ''}`}
      style={{ background: cfg.bg }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <div className="os-node-type-tag">{cfg.label}</div>
      <div className="os-node-icon">
        <i className={cfg.icon}></i>
      </div>
      <div className="os-node-label" title={label}>{shortLabel}</div>
      {subLine && <div className="os-node-sub">{subLine}</div>}
    </div>
  );
};

const nodeTypes = { omniNode: OmniNode };

// ── Layout Helper ──────────────────────────────────────────

/**
 * Simple force-free hierarchical layout.
 * Nodes are arranged in concentric rings based on their connection degree.
 */
const layoutNodes = (rawNodes, rawEdges) => {
  if (!rawNodes.length) return { rfNodes: [], rfEdges: [] };

  // Count connections per node
  const degreeMap = {};
  rawEdges.forEach(e => {
    degreeMap[e.source] = (degreeMap[e.source] || 0) + 1;
    degreeMap[e.target] = (degreeMap[e.target] || 0) + 1;
  });

  // Sort by degree desc → highest degree goes in the center
  const sorted = [...rawNodes].sort(
    (a, b) => (degreeMap[b.id] || 0) - (degreeMap[a.id] || 0)
  );

  const positions = {};
  const RING_SIZES = [1, 6, 12, 18, 24, 32, 40];
  const RING_RADII = [0, 220, 400, 560, 700, 840, 980];
  let nodeIndex = 0;

  for (let ring = 0; ring < RING_SIZES.length; ring++) {
    const count = Math.min(RING_SIZES[ring], sorted.length - nodeIndex);
    if (count <= 0) break;
    const radius = RING_RADII[ring];

    for (let i = 0; i < count; i++) {
      const angle = ring === 0 ? 0 : (2 * Math.PI * i) / RING_SIZES[ring];
      positions[sorted[nodeIndex].id] = {
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      };
      nodeIndex++;
    }
  }

  // Any remaining nodes in a wide final ring
  for (let i = nodeIndex; i < sorted.length; i++) {
    const angle = (2 * Math.PI * (i - nodeIndex)) / Math.max(sorted.length - nodeIndex, 1);
    positions[sorted[i].id] = {
      x: 1100 * Math.cos(angle),
      y: 1100 * Math.sin(angle),
    };
  }

  const rfNodes = rawNodes.map(n => {
    const cfg = getNodeConfig(n.type);
    const d = n.data || {};

    // Build subLabel based on type
    let subLabel = null;
    if (n.type === 'listing') {
      subLabel = [d.price, d.location].filter(Boolean).join(' · ') || null;
    } else if (n.type === 'market_index') {
      subLabel = [d.zone, d.region].filter(Boolean).join(', ') || null;
    } else if (n.type === 'document') {
      subLabel = d.provider || null;
    } else {
      subLabel = d.entity_type || d.type_label || null;
    }

    return {
      id: String(n.id),
      type: 'omniNode',
      position: positions[n.id] || { x: 0, y: 0 },
      data: {
        label: n.label || String(n.id),
        type: n.type,
        subLabel,
        color: cfg.color,
        originalData: d,
        nodeId: n.id,
      },
    };
  });

  const rfEdges = rawEdges.map((e, idx) => {
    const style = getEdgeStyle(e.type);
    return {
      id: e.id || `edge-${idx}`,
      source: String(e.source),
      target: String(e.target),
      label: e.label || undefined,
      type: 'smoothstep',
      style: style,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: style.stroke,
        width: 12,
        height: 12,
      },
      labelStyle: {
        fill: 'var(--text-secondary)',
        fontSize: 10,
        fontWeight: 600,
      },
      labelBgStyle: {
        fill: 'var(--bg-secondary)',
        fillOpacity: 0.85,
        rx: 4,
        ry: 4,
      },
      labelBgPadding: [6, 3],
      labelShowBg: !!e.label,
      data: { type: e.type },
    };
  });

  return { rfNodes, rfEdges };
};

// ── Node Detail Sidebar ────────────────────────────────────

// Returns true for any knowledge-graph node type (entities, persons, orgs, etc.)
const isKnowledgeNodeType = (type) => {
  const t = (type || '').toLowerCase().replace(/[^a-z_]/g, '');
  return !['listing', 'document', 'market_index', 'community'].includes(t);
};

const NodeDetailSidebar = ({ node, allEdges, allNodes, onClose, onViewEntity, onViewCommunity, onViewDocument, docLoadingId }) => {
  const { pinItem } = usePinToBoard();
  const pinBtnRef = useRef(null);

  if (!node) return null;

  const { label, type, originalData: d = {} } = node.data;
  const cfg = getNodeConfig(type);
  const itemType = getOmniItemType(type);

  // Edges connected to this node
  const connected = allEdges.filter(
    e => e.source === node.id || e.target === node.id
  );

  // Type-specific data rows
  const typeRows = [];
  if (type === 'listing') {
    if (d.price)       typeRows.push({ k: 'Price',    v: d.price });
    if (d.location)    typeRows.push({ k: 'Location', v: d.location });
    if (d.area)        typeRows.push({ k: 'Area',     v: d.area });
    if (d.type)        typeRows.push({ k: 'Type',     v: d.type });
    if (d.provider)    typeRows.push({ k: 'Provider', v: d.provider });
    if (d.keyword)     typeRows.push({ k: 'Keyword',  v: d.keyword });
  } else if (type === 'document') {
    if (d.file_name)   typeRows.push({ k: 'File',     v: d.file_name });
    if (d.provider)    typeRows.push({ k: 'Provider', v: d.provider });
    if (d.source_type) typeRows.push({ k: 'Type',     v: d.source_type });
  } else if (type === 'market_index') {
    if (d.zone)        typeRows.push({ k: 'Zone',     v: d.zone });
    if (d.region)      typeRows.push({ k: 'Region',   v: d.region });
    if (d.value)       typeRows.push({ k: 'Value',    v: String(d.value) });
    if (d.date_period) typeRows.push({ k: 'Period',   v: d.date_period });
  } else {
    // knowledge node
    if (d.entity_type) typeRows.push({ k: 'Entity type', v: d.entity_type });
    if (d.confidence)  typeRows.push({ k: 'Confidence',  v: `${Math.round(d.confidence * 100)}%` });
    if (d.community_id) typeRows.push({ k: 'Community',  v: String(d.community_id) });
  }

  const description = d.description || d.summary || d.context || null;
  const url         = d.url || null;

  return (
    <div className="os-sidebar">
      <div className="os-sidebar-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="os-sidebar-title" title={label}>{label.length > 60 ? label.slice(0, 57) + '…' : label}</p>
          <span
            className="os-sidebar-type-chip"
            style={{ background: cfg.bg, color: '#fff' }}
          >
            <i className={cfg.icon} style={{ fontSize: '0.62rem' }}></i>
            {cfg.label}
          </span>
        </div>
        <button className="os-sidebar-close" onClick={onClose} title="Close">
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="os-sidebar-body">
        {/* ── Pin to Board — shown at top so it's always visible ── */}
        {itemType && (
          <div className="os-detail-section">
            <button
              ref={pinBtnRef}
              className="os-pin-btn"
              onClick={() =>
                pinItem(
                  {
                    item_type: itemType,
                    ref_id: String(node.data.nodeId),
                    title: label,
                    metadata: d,
                  },
                  pinBtnRef.current,
                )
              }
            >
              <i className="fas fa-thumbtack" /> Pin to Board
            </button>
          </div>
        )}

        {typeRows.length > 0 && (
          <div className="os-detail-section">
            <div className="os-detail-section-label">Details</div>
            {typeRows.map(({ k, v }) => (
              <div className="os-detail-row" key={k}>
                <span className="os-detail-key">{k}</span>
                <span className="os-detail-val">{v}</span>
              </div>
            ))}
          </div>
        )}

        {description && (
          <div className="os-detail-section">
            <div className="os-detail-section-label">Description</div>
            <p className="os-detail-desc">{description}</p>
          </div>
        )}

        {url && (
          <div className="os-detail-section">
            <a href={url} target="_blank" rel="noopener noreferrer" className="os-detail-link">
              <i className="fas fa-external-link-alt"></i> Open source
            </a>
          </div>
        )}

        {connected.length > 0 && (
          <div className="os-detail-section">
            <div className="os-detail-section-label">
              Connections ({connected.length})
            </div>
            <div className="os-conn-list">
              {connected.map(e => {
                const otherId = e.source === node.id ? e.target : e.source;
                const other = allNodes.find(n2 => n2.id === otherId);
                const otherLabel = other?.data?.label || otherId;
                return (
                  <div key={e.id} className="os-conn-row">
                    <div style={{ fontWeight: 600 }}>{otherLabel.length > 35 ? otherLabel.slice(0, 33) + '…' : otherLabel}</div>
                    <div className="os-conn-label">{e.label || e.data?.type || 'edge'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── View Detail Actions ── */}
        {isKnowledgeNodeType(type) && onViewEntity && (
          <div className="os-detail-section">
            <button
              className="os-view-btn os-view-btn--entity"
              onClick={() => onViewEntity(node)}
            >
              <i className="fas fa-expand-alt"></i> View Entity Context
            </button>
          </div>
        )}

        {type === 'community' && onViewCommunity && (
          <div className="os-detail-section">
            <button
              className="os-view-btn os-view-btn--community"
              onClick={() => onViewCommunity(node)}
            >
              <i className="fas fa-diagram-project"></i> View Community
            </button>
          </div>
        )}

        {type === 'document' && onViewDocument && (
          <div className="os-detail-section">
            <button
              className="os-view-btn os-view-btn--document"
              onClick={() => onViewDocument(node)}
              disabled={docLoadingId === String(node.data.nodeId)}
            >
              {docLoadingId === String(node.data.nodeId)
                ? <><i className="fas fa-circle-notch fa-spin"></i> Loading DNA…</>
                : <><i className="fas fa-dna"></i> View Document DNA</>
              }
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

// ── Graph Inner ────────────────────────────────────────────

const GraphInner = ({ rfNodes: initNodes, rfEdges: initEdges, onNodeClick }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);

  // Sync when data changes
  React.useEffect(() => { setNodes(initNodes); }, [initNodes, setNodes]);
  React.useEffect(() => { setEdges(initEdges); }, [initEdges, setEdges]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      onNodeClick={(_, n) => onNodeClick(n)}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      deleteKeyCode={null}
      proOptions={{ hideAttribution: true }}
    >
      <Background color="var(--border-color, #e5e7eb)" gap={24} size={1} />
      <Controls showInteractive={false} />
      <MiniMap
        nodeColor={n => {
          const cfg = getNodeConfig(n.data?.type);
          return cfg.minimap;
        }}
        maskColor="rgba(0,0,0,0.08)"
        style={{ borderRadius: 8 }}
      />
    </ReactFlow>
  );
};

// ── Legend ─────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { type: 'listing',      label: 'Listing' },
  { type: 'entity',       label: 'Knowledge node' },
  { type: 'document',     label: 'Document' },
  { type: 'market_index', label: 'Market index' },
  { type: 'community',    label: 'Community' },
];

const GraphLegend = () => (
  <div className="os-legend">
    {LEGEND_ITEMS.map(({ type, label }) => {
      const cfg = getNodeConfig(type);
      return (
        <div className="os-legend-item" key={type}>
          <div className="os-legend-dot" style={{ background: cfg.color }}></div>
          <span>{label}</span>
        </div>
      );
    })}
  </div>
);

// ── Stats Bar ──────────────────────────────────────────────

const StatsBar = ({ stats, query }) => {
  if (!stats) return null;
  return (
    <div className="os-stats-bar">
      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginRight: 4 }}>
        Results for <strong style={{ color: 'var(--text-primary)' }}>"{query}"</strong>:
      </span>
      {stats.listings_found > 0 && (
        <span className="os-stat-chip listing">
          <i className="fas fa-building"></i> {stats.listings_found} listings
        </span>
      )}
      {stats.knowledge_nodes_found > 0 && (
        <span className="os-stat-chip knowledge">
          <i className="fas fa-circle-nodes"></i> {stats.knowledge_nodes_found} entities
        </span>
      )}
      {stats.documents_found > 0 && (
        <span className="os-stat-chip document">
          <i className="fas fa-file-alt"></i> {stats.documents_found} documents
        </span>
      )}
      {stats.market_index_found > 0 && (
        <span className="os-stat-chip market">
          <i className="fas fa-chart-line"></i> {stats.market_index_found} market indices
        </span>
      )}
      {stats.total_edges > 0 && (
        <span className="os-stat-chip edge">
          <i className="fas fa-arrows-left-right"></i> {stats.total_edges} edges
        </span>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────

const OmniSearch = () => {
  const [query, setQuery]           = useState('');
  const [depth, setDepth]           = useState(1);
  const [limit, setLimit]           = useState(30);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [rfNodes, setRfNodes]       = useState([]);
  const [rfEdges, setRfEdges]       = useState([]);
  const [stats, setStats]           = useState(null);
  const [lastQuery, setLastQuery]   = useState('');
  const [selectedNode, setSelectedNode] = useState(null);

  // ── Detail overlay states ──
  const [activeEntity,       setActiveEntity]       = useState(null); // { entityId, entityName, entityType }
  const [activeCommunity,    setActiveCommunity]    = useState(null); // community object { id, title, ... }
  const [activeDocument,     setActiveDocument]     = useState(null); // doc object
  const [docLoadingId,       setDocLoadingId]       = useState(null); // nodeId while fetching full doc data

  const inputRef = useRef(null);

  // ── Handlers that open the detail overlays from the sidebar ──
  const handleViewEntity = useCallback((node) => {
    const d = node.data.originalData || {};
    setActiveEntity({
      entityId:   String(node.data.nodeId),
      entityName: node.data.label,
      entityType: d.entity_type || d.type_label || 'UNKNOWN',
    });
  }, []);

  const handleViewCommunity = useCallback((node) => {
    const d = node.data.originalData || {};
    // The graph engine often prepends "community_" to the node ID for communities.
    // The backend expects a raw UUID. Strip the prefix if present.
    let rawId = String(node.data.nodeId);
    if (rawId.startsWith('community_')) {
      rawId = rawId.replace('community_', '');
    }
    
    setActiveCommunity({
      id:      rawId,
      title:   node.data.label,
      summary: d.summary || d.description || '',
      rating:  d.rating ?? d.community_rating ?? 0,
    });
  }, []);

  const handleViewDocument = useCallback(async (node) => {
    const d = node.data.originalData || {};
    // Resolve the real database ID using the same priority chain as DocumentDNAList:
    //   pdf_id  → source_id  → id  → nodeId (graph node id, last resort)
    // This avoids passing a graph-internal ID (e.g. "document_abc123") to the
    // backend which expects the actual pdf / source record primary key.
    const docId = d.pdf_id ?? d.source_id ?? d.id ?? node.data.nodeId;

    // Build a baseline doc from what the graph node already carries.
    // This is shown immediately as a fallback if the fetch fails.
    const fallbackDoc = {
      pdf_id:                  docId,
      id:                      docId,
      file_name:               d.file_name || node.data.label,
      provider:                d.provider              || null,
      processing_status:       d.processing_status     || 'processed',
      community_distribution:  d.community_distribution || [],
      total_edges_contributed: d.total_edges_contributed ?? 0,
      url:                     d.url                   || null,
      created_date:            d.created_date          || null,
      last_processed_at:       d.last_processed_at     || null,
      description:             d.description || d.summary || null,
    };

    // Show loading on the button in the sidebar
    setDocLoadingId(String(node.data.nodeId));

    try {
      // Fetch the full document list (same API used by DocumentDNAList / KnowledgeGraphPage)
      // to get the complete community_distribution, edges, and processing metadata.
      const res = await fetchDocumentImpact();
      if (res.success && Array.isArray(res.data)) {
        const docIdStr = String(docId);
        // Match by any of the ID fields the API may return
        const fullDoc = res.data.find(doc =>
          String(doc.pdf_id)    === docIdStr ||
          String(doc.source_id) === docIdStr ||
          String(doc.id)        === docIdStr
        );
        setActiveDocument(fullDoc || fallbackDoc);
      } else {
        // API failed — still open the detail panel with whatever we have
        setActiveDocument(fallbackDoc);
      }
    } catch {
      setActiveDocument(fallbackDoc);
    } finally {
      setDocLoadingId(null);
    }
  }, []);

  const handleSearch = useCallback(async (q = query) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    setStats(null);

    try {
      const res = await omniSearch(trimmed, depth, limit);
      if (res.success !== false) {
        const graph = res.graph || {};
        const { rfNodes: rn, rfEdges: re } = layoutNodes(
          graph.nodes || [],
          graph.edges || []
        );
        setRfNodes(rn);
        setRfEdges(re);
        setStats(res.stats || null);
        setLastQuery(trimmed);
      } else {
        setError(res.message || 'Search failed. Please try again.');
        setRfNodes([]);
        setRfEdges([]);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setRfNodes([]);
      setRfEdges([]);
    } finally {
      setLoading(false);
    }
  }, [query, depth, limit]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const hasResults = rfNodes.length > 0;

  return (
    <div className="os-page">
      {/* ── Hero Search ── */}
      <div className="os-hero">
        <h2 className="os-hero-title">
          <i className="fas fa-magnifying-glass-chart"></i>
          Omni Search
        </h2>
        <p className="os-hero-subtitle">
          Search across all data sources simultaneously — listings, entities, documents & market indices
        </p>

        <div className="os-search-row">
          <input
            ref={inputRef}
            className="os-search-input"
            type="text"
            placeholder='e.g. "Athens", "3-bedroom", "legal report" …'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <button
            className="os-search-btn"
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
          >
            {loading
              ? <><i className="fas fa-circle-notch fa-spin"></i> Searching…</>
              : <><i className="fas fa-magnifying-glass"></i> Search</>
            }
          </button>
        </div>

        <div className="os-controls">
          <label className="os-control-label">
            Graph depth:
            <select value={depth} onChange={e => setDepth(Number(e.target.value))}>
              <option value={1}>1 — direct connections</option>
              <option value={2}>2 — second-degree</option>
            </select>
          </label>
          <label className="os-control-label">
            Max nodes:
            <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="os-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}

      {/* ── Stats Bar (shown after search) ── */}
      {hasResults && <StatsBar stats={stats} query={lastQuery} />}

      {/* ── Main Area ── */}
      <div className="os-main">
        {/* Graph */}
        <div className="os-graph-wrap">
          {loading && (
            <div className="os-loading-overlay">
              <div className="os-spinner"></div>
              Searching across all data sources…
            </div>
          )}

          {!loading && !hasResults && (
            <div className="os-graph-empty">
              <i className="fas fa-diagram-project"></i>
              <h3>{lastQuery ? 'No results found' : 'Start your search'}</h3>
              <p>
                {lastQuery
                  ? `No data matched "${lastQuery}". Try a different search term.`
                  : 'Type any term — a property, a location, an entity or a concept — and explore the connections.'
                }
              </p>
            </div>
          )}

          {!loading && hasResults && (
            <ReactFlowProvider>
              <GraphInner
                rfNodes={rfNodes}
                rfEdges={rfEdges}
                onNodeClick={setSelectedNode}
              />
              <GraphLegend />
            </ReactFlowProvider>
          )}
        </div>

        {/* Sidebar (only when a node is selected) */}
        {selectedNode && (
          <NodeDetailSidebar
            node={selectedNode}
            allNodes={rfNodes}
            allEdges={rfEdges}
            onClose={() => setSelectedNode(null)}
            onViewEntity={handleViewEntity}
            onViewCommunity={handleViewCommunity}
            onViewDocument={handleViewDocument}
            docLoadingId={docLoadingId}
          />
        )}
      </div>

      {/* ── Entity Context Window overlay ── */}
      {activeEntity && (
        <EntityContextWindow
          entityId={activeEntity.entityId}
          entityName={activeEntity.entityName}
          entityType={activeEntity.entityType}
          onClose={() => setActiveEntity(null)}
        />
      )}

      {/* ── Community Detail overlay ── */}
      {activeCommunity && (
        <div className="os-detail-overlay">
          <div className="os-detail-overlay-inner">
            <CommunityDetailPanel
              community={activeCommunity}
              onBack={() => setActiveCommunity(null)}
              onOpenEntity={(entityNode) => {
                setActiveCommunity(null);
                setActiveEntity({
                  entityId:   String(entityNode.id || entityNode.node_id),
                  entityName: entityNode.entity_name || entityNode.name || entityNode.label || '',
                  entityType: entityNode.entity_type || entityNode.type || 'UNKNOWN',
                });
              }}
            />
          </div>
        </div>
      )}

      {/* ── Document DNA Detail overlay ── */}
      {activeDocument && (
        <div className="os-detail-overlay">
          <div className="os-detail-overlay-inner">
            <DocumentDNADetail
              doc={activeDocument}
              onBack={() => setActiveDocument(null)}
              onSelectCommunity={(comm) => {
                setActiveDocument(null);
                setActiveCommunity({ id: comm.id, title: comm.title });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OmniSearch;
